/**
 * Organizations Management Page
 * Admin-sida för att hantera organisationer med hierarkisk struktur
 * 
 * Refaktorerad: State och logik flyttat till useOrganizationsData hook
 * Formulärfält extraherat till OrganizationFormFields komponent
 */

import { useEffect, useState } from 'react';
import { ROUTES } from '@/constants/routes';
import { useNavigate } from 'react-router-dom';
import { useTranslation } from '@/store/languageStore';
import { useAuthStore } from '@/store/authStore';
import {
  ArrowDownTrayIcon,
  ArrowLeftIcon,
  ArrowsRightLeftIcon,
  ArrowUpTrayIcon,
  BuildingOfficeIcon,
  MagnifyingGlassIcon,
  PencilSquareIcon,
  PlusCircleIcon,
  TrashIcon,
} from '@heroicons/react/24/outline';
import { useOrganizationsData } from '@/hooks/useOrganizationsData';
import { usePermissions } from '@/hooks/usePermissions';
import { useConfirmDialog } from '@/hooks/useConfirmDialog';
import OrganizationFormFields from '@/components/organizations/OrganizationFormFields';
import OrganizationContactRoles from '@/components/organizations/OrganizationContactRoles';
import OrganizationCabinetSettings from '@/components/organizations/OrganizationCabinetSettings';
import OrganizationFuelAccounts from '@/components/organizations/OrganizationFuelAccounts';
import OrganizationImportModal from '@/components/organizations/OrganizationImportModal';
import OrganizationAutoServiceSettings from '@/components/organizations/OrganizationAutoServiceSettings';
import OrganizationDrivingLogSettings from '@/components/organizations/OrganizationDrivingLogSettings';
import OrganizationReminderSettings from '@/components/organizations/OrganizationReminderSettings';
import OrganizationReportModels from '@/components/organizations/OrganizationReportModels';
import OrganizationBookingPolicyEditor from '@/components/organizations/OrganizationBookingPolicyEditor';
import LogoCropperModal from '@/components/organizations/LogoCropperModal';
import HierarchicalOrgSelect from '@/components/common/HierarchicalOrgSelect';
import api from '@/services/api/client';
import toast from 'react-hot-toast';
import type { Organization, OrganizationWithChildren } from '@/types/organizations';
import { isOrgTopNode } from '@/utils/organizationFlags';

const OrganizationsManagement = () => {
  const navigate = useNavigate();
  const { t } = useTranslation();
  const isGlobalAdmin = useAuthStore((state) => {
    const flag = state.user?.is_global_admin;
    return flag === true || flag === 1 || flag === '1';
  });
  const { confirm, ConfirmDialog } = useConfirmDialog();
  const { permissions, isUnlimited } = usePermissions();
  const canImportOrganizations =
    isUnlimited ||
    (permissions?.permissions?.some(
      (p) => p.feature.code === 'ADMIN_ORGANIZATIONS' && p.action?.code === 'IMPORT'
    ) ??
      false);
  const [logoPreviewUrl, setLogoPreviewUrl] = useState<string | null>(null);
  const [logoLoading, setLogoLoading] = useState(false);
  const [logoUploading, setLogoUploading] = useState(false);
  const [showLogoCropper, setShowLogoCropper] = useState(false);
  const [editModalTab, setEditModalTab] = useState<
    | 'contactRoles'
    | 'customerLogo'
    | 'additionalInfo'
    | 'authSettings'
    | 'fuelCompanies'
    | 'bookingSettings'
    | 'cabinetSettings'
    | 'automaticService'
    | 'drivingLogSettings'
    | 'reminders'
    | 'reportModels'
  >('additionalInfo');
  const [importTargetOrg, setImportTargetOrg] = useState<Organization | null>(null);

  // All state and logic from custom hook
  const {
    topNodes,
    totalTopNodes,
    totalTopNodePages,
    currentPage,
    pageSize,
    searchInput,
    handleSearch,
    expandedNodes,
    hierarchies,
    directChildren,
    loading,
    showCreateModal,
    setShowCreateModal,
    showEditModal,
    setShowEditModal,
    showMoveModal,
    setShowMoveModal,
    selectedOrganization,
    moveTargetParentId,
    setMoveTargetParentId,
    moveExcludeIds,
    parentOptions,
    parentSearchTerm,
    setParentSearchTerm,
    parentPage,
    parentTotalPages,
    parentTotal,
    loadingParents,
    loadParentOptions,
    formData,
    setFormData,
    resetFormData,
    handleCreateOrganization,
    handleUpdateOrganization,
    handleDeleteOrganization,
    handleMoveOrganization,
    toggleExpand,
    openEditModal,
    openCreateModal,
    openMoveModal,
    goToPage,
  } = useOrganizationsData();

  const editIsTopNode =
    isOrgTopNode(selectedOrganization) || Boolean(formData.is_top_node);

  // Nollställ flik och hämta logotyp-URL när redigeringsmodal öppnas
  useEffect(() => {
    if (!showEditModal) {
      setEditModalTab('additionalInfo');
      setLogoPreviewUrl(null);
      return;
    }
    if (!editIsTopNode || !selectedOrganization) {
      setLogoPreviewUrl(null);
      return;
    }
    const orgId = selectedOrganization.id;
    let cancelled = false;
    setLogoLoading(true);
    api
      .get<{ success: boolean; data: { url: string | null } }>(`/api/organizations/${orgId}/logo-url`)
      .then((res) => {
        if (!cancelled && res.data.success && res.data.data?.url) {
          setLogoPreviewUrl(res.data.data.url);
        } else {
          setLogoPreviewUrl(null);
        }
      })
      .catch(() => {
        if (!cancelled) setLogoPreviewUrl(null);
      })
      .finally(() => {
        if (!cancelled) setLogoLoading(false);
      });
    return () => {
      cancelled = true;
    };
  }, [showEditModal, selectedOrganization?.id, editIsTopNode]);

  useEffect(() => {
    if (!showEditModal || !selectedOrganization) return;

    if (
      !editIsTopNode &&
      (editModalTab === 'fuelCompanies' ||
        editModalTab === 'customerLogo' ||
        editModalTab === 'bookingSettings' ||
        editModalTab === 'authSettings' ||
        editModalTab === 'cabinetSettings' ||
        editModalTab === 'automaticService' ||
        editModalTab === 'drivingLogSettings' ||
        editModalTab === 'reminders' ||
        editModalTab === 'reportModels')
    ) {
      setEditModalTab('additionalInfo');
    }
  }, [showEditModal, selectedOrganization, editModalTab, editIsTopNode]);

  const handleSaveCroppedLogo = async (blob: Blob) => {
    if (!editIsTopNode || !selectedOrganization) return;
    const orgId = selectedOrganization.id;
    setLogoUploading(true);
    try {
      const formData = new FormData();
      formData.append('file', blob, 'logo.png');
      const res = await api.post<{ success: boolean; data?: { logoStoragePath: string }; message?: string }>(
        `/api/organizations/${orgId}/logo`,
        formData,
        { headers: { 'Content-Type': 'multipart/form-data' } },
      );
      if (res.data.success) {
        toast.success(t('organizations.logoUploadSuccess') ?? 'Logotyp uppladdad');
        const urlRes = await api.get<{ success: boolean; data: { url: string | null } }>(
          `/api/organizations/${orgId}/logo-url`,
        );
        if (urlRes.data.data?.url) setLogoPreviewUrl(urlRes.data.data.url);
        setShowLogoCropper(false);
      } else {
        toast.error((res.data as any).error ?? 'Kunde inte ladda upp');
      }
    } catch (err: any) {
      toast.error(err.response?.data?.error ?? err.message ?? 'Kunde inte ladda upp logotyp');
    } finally {
      setLogoUploading(false);
    }
  };

  const handleDeleteLogo = async () => {
    if (!editIsTopNode || !selectedOrganization) return;
    try {
      await api.delete(`/api/organizations/${selectedOrganization.id}/logo`);
      toast.success(t('organizations.logoDeleteSuccess') ?? 'Logotyp borttagen');
      setLogoPreviewUrl(null);
    } catch (err: any) {
      toast.error(err.response?.data?.error ?? 'Kunde inte ta bort logotyp');
    }
  };

  const handleDownloadTemplate = (org: Organization) => {
    const a = document.createElement('a');
    // Token skickas inte via <a>-tag; vi använder fetch + blob för autentiserade anrop
    api
      .get(`/api/organizations/import/template?orgId=${org.id}`, { responseType: 'blob' })
      .then((res) => {
        const blob = new Blob([res.data], {
          type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet',
        });
        const blobUrl = URL.createObjectURL(blob);
        a.href = blobUrl;
        const safeName = org.name.replace(/[^a-zA-ZåäöÅÄÖ0-9_-]/g, '_');
        a.download = `Importmall_${safeName}.xlsx`;
        a.click();
        URL.revokeObjectURL(blobUrl);
      })
      .catch(() => toast.error(t('organizations.importError')));
  };

  // Render organization tree recursively
  const renderOrganizationTree = (org: Organization | OrganizationWithChildren, depth: number = 0): JSX.Element => {
    const orgKey = String(org.id);
    const isExpanded = expandedNodes.has(orgKey);
    const orgWithChildren = org as OrganizationWithChildren;
    
    const hasChildrenFromOrg = orgWithChildren.children && orgWithChildren.children.length > 0;
    const hierarchy = hierarchies.get(orgKey);
    const children = directChildren.get(orgKey) || [];

    // Ocachade direkta barn vinner över hierarki-cachen, annars döljs en nyss skapad nod.
    const displayChildren = children.length > 0
      ? children
      : (hasChildrenFromOrg ? orgWithChildren.children! : (hierarchy?.children || []));
    
    const hasChildren = displayChildren.length > 0 
      || (orgWithChildren.direct_children_count && orgWithChildren.direct_children_count > 0)
      || (hierarchy?.direct_children_count && hierarchy.direct_children_count > 0);

    return (
      <div key={org.id}>
        <div
          className={`flex items-center gap-2 p-2 rounded hover:bg-bgElevated ${
            depth === 0 ? 'font-semibold' : ''
          }`}
          style={{ paddingLeft: `${depth * 20 + 8}px` }}
        >
          {hasChildren ? (
            <button
              onClick={() => toggleExpand(org.id, orgWithChildren)}
              className="text-textSecondary hover:text-textDefault w-4"
            >
              {isExpanded ? '▼' : '▶'}
            </button>
          ) : (
            <span className="w-4" />
          )}
          <span className="flex-1 text-textDefault">{org.name}</span>
          {isOrgTopNode(org) && (
            <span className="text-xs px-2 py-1 bg-bgPrimary text-textLight rounded">
              {depth > 0 ? t('organizations.customerTopNode') : t('organizations.topNode')}
            </span>
          )}
          <span
            className={`text-xs px-2 py-1 rounded ${
              org.status === 'ACTIVE'
                ? 'bg-green-100 text-green-800'
                : org.status === 'SUSPENDED'
                ? 'bg-yellow-100 text-yellow-800'
                : 'bg-red-100 text-red-800'
            }`}
          >
            {org.status === 'ACTIVE' ? t('organizations.active') : org.status === 'SUSPENDED' ? t('organizations.suspended') : t('organizations.inactive')}
          </span>
          {isOrgTopNode(org) && canImportOrganizations && (
            <>
              <button
                onClick={() => handleDownloadTemplate(org)}
                title={t('organizations.downloadTemplateTooltip')}
                aria-label={t('organizations.downloadTemplate')}
                className="p-1.5 bg-emerald-100 text-emerald-800 rounded hover:bg-emerald-200"
              >
                <ArrowDownTrayIcon className="w-4 h-4" />
              </button>
              <button
                onClick={() => setImportTargetOrg(org)}
                title={t('organizations.importOrganizationsTooltip')}
                aria-label={t('organizations.importOrganizations')}
                className="p-1.5 bg-blue-100 text-blue-800 rounded hover:bg-blue-200"
              >
                <ArrowUpTrayIcon className="w-4 h-4" />
              </button>
            </>
          )}
          <button
            onClick={() => openEditModal(org)}
            title={t('common.edit')}
            aria-label={t('common.edit')}
            className="p-1.5 bg-bgLight text-textDefault rounded hover:bg-bgPrimary hover:text-textLight"
          >
            <PencilSquareIcon className="w-4 h-4" />
          </button>
          <button
            onClick={() => openMoveModal(org)}
            title={t('organizations.move') || 'Flytta'}
            aria-label={t('organizations.move') || 'Flytta'}
            className="p-1.5 bg-blue-100 text-blue-800 rounded hover:bg-blue-200"
          >
            <ArrowsRightLeftIcon className="w-4 h-4" />
          </button>
          <button
            onClick={() => openCreateModal(org.id)}
            title={t('organizations.addSub')}
            aria-label={t('organizations.addSub')}
            className="p-1.5 bg-bgLight text-textDefault rounded hover:bg-bgPrimary hover:text-textLight"
          >
            <PlusCircleIcon className="w-4 h-4" />
          </button>
          <button
            onClick={() =>
              confirm({
                title: t('common.confirm') ?? 'Bekräfta',
                message: t('organizations.deleteConfirm') ?? 'Är du säker på att du vill ta bort denna organisation och alla dess underorganisationer?',
                onConfirm: () => handleDeleteOrganization(org.id),
                variant: 'danger',
              })
            }
            title={t('organizations.deleteOrganization')}
            aria-label={t('organizations.deleteOrganization')}
            className="p-1.5 bg-red-100 text-red-800 rounded hover:bg-red-200"
          >
            <TrashIcon className="w-4 h-4" />
          </button>
        </div>
        {isExpanded && displayChildren.length > 0 && (
          <div className="ml-4">
            {displayChildren.map(child => renderOrganizationTree(child, depth + 1))}
          </div>
        )}
      </div>
    );
  };

  return (
    <div className="p-4 bg-bgContainer rounded-lg h-full">
      <div className="flex justify-between items-center mb-6">
        <div className="flex items-center gap-3">
          <button onClick={() => navigate(ROUTES.SYSTEM_ADMINISTRATION)} className="p-2 hover:bg-bgElevated rounded-lg transition-colors">
            <ArrowLeftIcon className="w-5 h-5 text-textDefault" />
          </button>
          <h1 className="text-2xl font-bold text-textDefault flex items-center gap-2">
            <BuildingOfficeIcon className="w-6 h-6 text-primary" />
            {t('organizations.title')}
          </h1>
        </div>
        <div className="flex items-center gap-3">
          <div className="relative">
            <MagnifyingGlassIcon className="w-5 h-5 text-textTertiary absolute left-3 top-1/2 -translate-y-1/2" />
            <input
              type="text"
              value={searchInput}
              onChange={(e) => handleSearch(e.target.value)}
              placeholder={t('organizations.searchPlaceholder') || 'Sök organisation...'}
              className="pl-10 pr-4 py-2 border border-border rounded-lg bg-bgElevated text-textDefault placeholder-textTertiary w-64 focus:outline-none focus:ring-2 focus:ring-bgPrimary focus:border-bgPrimary"
            />
          </div>
          <button
            onClick={() => openCreateModal()}
            className="px-4 py-2 bg-bgPrimary text-textLight rounded-lg hover:opacity-90 transition-opacity"
          >
            {t('organizations.createTopNode')}
          </button>
        </div>
      </div>

      {/* Organization list */}
      <div className="space-y-2">
        {loading ? (
          <div className="text-textTertiary p-4">{t('common.loading')}</div>
        ) : topNodes.length === 0 ? (
          <div className="text-textSecondary p-4 text-center">
            {t('organizations.noOrganizations')}
          </div>
        ) : (
          <>
            {topNodes.map(org => renderOrganizationTree(org, 0))}
            
            {/* Pagination for top nodes */}
            {totalTopNodes > pageSize && (
              <div className="flex items-center justify-between mt-6 pt-4 border-t border-border">
                <button
                  onClick={() => goToPage(currentPage - 1)}
                  disabled={currentPage === 1}
                  className="px-4 py-2 border border-border rounded-lg bg-bgElevated text-textDefault disabled:opacity-50 disabled:cursor-not-allowed hover:bg-bgPrimary hover:text-textLight transition-colors"
                >
                  {t('common.previous') || 'Föregående'}
                </button>
                <span className="text-sm text-textSecondary">
                  {t('common.page') || 'Sida'} {currentPage} / {totalTopNodePages} ({totalTopNodes} {t('common.total') || 'totalt'})
                </span>
                <button
                  onClick={() => goToPage(currentPage + 1)}
                  disabled={currentPage >= totalTopNodePages}
                  className="px-4 py-2 border border-border rounded-lg bg-bgElevated text-textDefault disabled:opacity-50 disabled:cursor-not-allowed hover:bg-bgPrimary hover:text-textLight transition-colors"
                >
                  {t('common.next') || 'Nästa'}
                </button>
              </div>
            )}
          </>
        )}
      </div>

      {/* Create Modal */}
      {showCreateModal && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
          <div className="bg-bgContainer rounded-lg p-6 max-w-md w-full max-h-[90vh] overflow-y-auto">
            <h2 className="text-2xl font-bold text-textDefault mb-4">
              {formData.parent_id ? t('organizations.createSubOrganization') : t('organizations.createTopNode')}
            </h2>

            <OrganizationFormFields
              formData={formData}
              setFormData={setFormData}
              showParentSelector={!formData.parent_id}
              parentOptions={parentOptions}
              parentSearchTerm={parentSearchTerm}
              setParentSearchTerm={setParentSearchTerm}
              parentPage={parentPage}
              parentTotalPages={parentTotalPages}
              parentTotal={parentTotal}
              loadingParents={loadingParents}
              onLoadParentPage={(page) => loadParentOptions(page)}
            />

            <div className="flex justify-end gap-2 mt-6">
              <button
                onClick={() => {
                  setShowCreateModal(false);
                  resetFormData();
                }}
                className="px-4 py-2 bg-bgLight text-textDefault rounded-lg hover:bg-bgElevated transition-colors"
              >
                {t('common.cancel')}
              </button>
              <button
                onClick={handleCreateOrganization}
                className="px-4 py-2 bg-bgPrimary text-textLight rounded-lg hover:opacity-90 transition-opacity"
              >
                {t('organizations.createButton')}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Edit Modal */}
      {showEditModal && selectedOrganization && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-3">
          <div className="bg-bgContainer rounded-lg p-6 w-[min(112rem,calc(100vw-1.5rem))] max-h-[min(90vh,calc(100dvh-1.5rem))] flex flex-col overflow-hidden min-h-0 shadow-xl">
            <div className="flex-1 min-h-0 flex flex-col overflow-hidden">
              <div className="flex-1 min-h-0 overflow-y-auto overscroll-contain pr-1 -mr-1">
            <h2 className="text-2xl font-bold text-textDefault mb-4 shrink-0">{t('organizations.editOrganization')}</h2>

            {/* Bild 1 överst: Namn, Beskrivning, Toppnod, Status */}
            <div className="mb-6 shrink-0">
              <OrganizationFormFields
                formData={formData}
                setFormData={setFormData}
                isTopNode={editIsTopNode}
                onlySections={['basic']}
              />
            </div>

            {/* Flikar i ordning: Ytterligare information | Kundlogotyp | Ansvariga / Kontaktroller | Autentiseringsinställningar */}
            <div className="flex border-b border-border mb-4 flex-nowrap gap-1 flex-shrink-0 overflow-x-auto pb-px sticky top-0 z-10 bg-bgContainer pt-1 -mt-1">
              <button
                type="button"
                onClick={() => setEditModalTab('additionalInfo')}
                className={`px-3 py-2 text-sm font-medium rounded-t whitespace-nowrap shrink-0 transition-colors ${
                  editModalTab === 'additionalInfo'
                    ? 'bg-bgElevated text-textDefault border border-border border-b-0 -mb-px'
                    : 'text-textSecondary hover:text-textDefault hover:bg-bgElevated'
                }`}
              >
                {t('organizations.additionalInfo')}
              </button>
              {editIsTopNode && (
                <button
                  type="button"
                  onClick={() => setEditModalTab('customerLogo')}
                  className={`px-3 py-2 text-sm font-medium rounded-t whitespace-nowrap shrink-0 transition-colors ${
                    editModalTab === 'customerLogo'
                      ? 'bg-bgElevated text-textDefault border border-border border-b-0 -mb-px'
                      : 'text-textSecondary hover:text-textDefault hover:bg-bgElevated'
                  }`}
                >
                  {t('organizations.customerLogo')}
                </button>
              )}
              {editIsTopNode && (
                <button
                  type="button"
                  onClick={() => setEditModalTab('fuelCompanies')}
                  className={`px-3 py-2 text-sm font-medium rounded-t whitespace-nowrap shrink-0 transition-colors ${
                    editModalTab === 'fuelCompanies'
                      ? 'bg-bgElevated text-textDefault border border-border border-b-0 -mb-px'
                      : 'text-textSecondary hover:text-textDefault hover:bg-bgElevated'
                  }`}
                >
                  {t('organizations.fuelCompanies.tabTitle')}
                </button>
              )}
              <button
                type="button"
                onClick={() => setEditModalTab('contactRoles')}
                className={`px-3 py-2 text-sm font-medium rounded-t whitespace-nowrap shrink-0 transition-colors ${
                  editModalTab === 'contactRoles'
                    ? 'bg-bgElevated text-textDefault border border-border border-b-0 -mb-px'
                    : 'text-textSecondary hover:text-textDefault hover:bg-bgElevated'
                }`}
              >
                {t('organizations.contactRoles.title')}
              </button>
              {editIsTopNode && (
                <button
                  type="button"
                  onClick={() => setEditModalTab('bookingSettings')}
                  className={`px-3 py-2 text-sm font-medium rounded-t whitespace-nowrap shrink-0 transition-colors ${
                    editModalTab === 'bookingSettings'
                      ? 'bg-bgElevated text-textDefault border border-border border-b-0 -mb-px'
                      : 'text-textSecondary hover:text-textDefault hover:bg-bgElevated'
                  }`}
                >
                  {t('organizations.bookingSettings')}
                </button>
              )}
              {editIsTopNode && (
                <button
                  type="button"
                  onClick={() => setEditModalTab('authSettings')}
                  className={`px-3 py-2 text-sm font-medium rounded-t whitespace-nowrap shrink-0 transition-colors ${
                    editModalTab === 'authSettings'
                      ? 'bg-bgElevated text-textDefault border border-border border-b-0 -mb-px'
                      : 'text-textSecondary hover:text-textDefault hover:bg-bgElevated'
                  }`}
                >
                  {t('organizations.authSettings')}
                </button>
              )}
              {editIsTopNode && (
                <button
                  type="button"
                  onClick={() => setEditModalTab('cabinetSettings')}
                  className={`px-3 py-2 text-sm font-medium rounded-t whitespace-nowrap shrink-0 transition-colors ${
                    editModalTab === 'cabinetSettings'
                      ? 'bg-bgElevated text-textDefault border border-border border-b-0 -mb-px'
                      : 'text-textSecondary hover:text-textDefault hover:bg-bgElevated'
                  }`}
                >
                  {t('organizations.cabinetSettings')}
                </button>
              )}
              {editIsTopNode && (
                <button
                  type="button"
                  onClick={() => setEditModalTab('automaticService')}
                  className={`px-3 py-2 text-sm font-medium rounded-t whitespace-nowrap shrink-0 transition-colors ${
                    editModalTab === 'automaticService'
                      ? 'bg-bgElevated text-textDefault border border-border border-b-0 -mb-px'
                      : 'text-textSecondary hover:text-textDefault hover:bg-bgElevated'
                  }`}
                >
                  {t('organizations.automaticService')}
                </button>
              )}
              {editIsTopNode && (
                <button
                  type="button"
                  onClick={() => setEditModalTab('drivingLogSettings')}
                  className={`px-3 py-2 text-sm font-medium rounded-t whitespace-nowrap shrink-0 transition-colors ${
                    editModalTab === 'drivingLogSettings'
                      ? 'bg-bgElevated text-textDefault border border-border border-b-0 -mb-px'
                      : 'text-textSecondary hover:text-textDefault hover:bg-bgElevated'
                  }`}
                >
                  {t('organizations.drivingLog.tabTitle')}
                </button>
              )}
              {editIsTopNode && (
                <button
                  type="button"
                  onClick={() => setEditModalTab('reminders')}
                  className={`px-3 py-2 text-sm font-medium rounded-t whitespace-nowrap shrink-0 transition-colors ${
                    editModalTab === 'reminders'
                      ? 'bg-bgElevated text-textDefault border border-border border-b-0 -mb-px'
                      : 'text-textSecondary hover:text-textDefault hover:bg-bgElevated'
                  }`}
                >
                  {t('organizations.reminders.tabTitle')}
                </button>
              )}
              {editIsTopNode && isGlobalAdmin && (
                <button
                  type="button"
                  onClick={() => setEditModalTab('reportModels')}
                  className={`px-3 py-2 text-sm font-medium rounded-t whitespace-nowrap shrink-0 transition-colors ${
                    editModalTab === 'reportModels'
                      ? 'bg-bgElevated text-textDefault border border-border border-b-0 -mb-px'
                      : 'text-textSecondary hover:text-textDefault hover:bg-bgElevated'
                  }`}
                >
                  {t('organizations.reportModels.tabTitle')}
                </button>
              )}
            </div>

            {/* Flikinnehåll */}
            <div className="min-h-0 pb-2">
            {editModalTab === 'additionalInfo' && (
              <div>
                <OrganizationFormFields
                  formData={formData}
                  setFormData={setFormData}
                  isTopNode={editIsTopNode}
                  onlySections={['additionalInfo']}
                />
              </div>
            )}

            {editModalTab === 'customerLogo' && editIsTopNode && (
              <div className="p-4 rounded-lg border border-border bg-bgElevated">
                <h3 className="text-sm font-medium text-textDefault mb-2">{t('organizations.customerLogo')}</h3>
                {logoLoading ? (
                  <p className="text-textTertiary text-sm">{t('common.loading')}</p>
                ) : (
                  <>
                    {logoPreviewUrl && (
                      <div className="flex items-center gap-3 mb-3">
                        <img
                          src={logoPreviewUrl}
                          alt=""
                          className="w-12 h-12 object-contain rounded border border-border"
                        />
                        <button
                          type="button"
                          onClick={handleDeleteLogo}
                          className="text-sm text-red-600 hover:underline"
                        >
                          {t('organizations.removeLogo')}
                        </button>
                      </div>
                    )}
                    <div className="flex items-center gap-2">
                      <button
                        type="button"
                        onClick={() => setShowLogoCropper(true)}
                        disabled={logoUploading}
                        className="px-3 py-1.5 text-sm bg-bgLight text-textDefault rounded hover:bg-bgPrimary hover:text-textLight disabled:opacity-50"
                      >
                        {logoUploading ? t('common.loading') : t('organizations.uploadLogo')}
                      </button>
                      <span className="text-xs text-textTertiary">JPEG, PNG, WebP, GIF, SVG, max 2 MB</span>
                    </div>
                  </>
                )}
              </div>
            )}

            {editModalTab === 'fuelCompanies' && editIsTopNode && (
              <div>
                <OrganizationFuelAccounts organizationId={selectedOrganization.id} />
              </div>
            )}

            {editModalTab === 'contactRoles' && (
              <div>
                <OrganizationContactRoles organizationId={selectedOrganization.id} />
              </div>
            )}

            {editModalTab === 'bookingSettings' && editIsTopNode && (
              <div className="p-4 rounded-lg border border-border bg-bgElevated space-y-4">
                <h3 className="text-sm font-medium text-textDefault mb-2">{t('organizations.bookingSettings')}</h3>

                <div>
                  <label className="flex items-start gap-2 cursor-pointer">
                    <input
                      type="checkbox"
                      checked={Boolean(formData.booking_default_timeline_view)}
                      onChange={(e) =>
                        setFormData((prev) => ({ ...prev, booking_default_timeline_view: e.target.checked }))
                      }
                      className="mt-1 h-4 w-4 rounded border-border text-primary focus:ring-primary"
                    />
                    <span className="text-sm font-medium text-textDefault">
                      {t('organizations.defaultTimelineView')}
                    </span>
                  </label>
                  <p className="mt-1 text-xs text-textTertiary">{t('organizations.defaultTimelineViewHelp')}</p>
                </div>

                <div>
                  <label className="block text-sm font-medium text-textDefault mb-1">{t('organizations.maxRecurringBookings')}</label>
                  <input
                    type="number"
                    min={0}
                    max={99}
                    value={formData.max_recurring_bookings ?? ''}
                    onChange={(e) => {
                      const v = e.target.value;
                      setFormData((prev) => ({
                        ...prev,
                        max_recurring_bookings: v === '' ? null : (parseInt(v, 10) ?? null),
                      }));
                    }}
                    placeholder="0"
                    className="w-full max-w-xs px-3 py-2 border border-border rounded-lg bg-bgContainer text-textDefault"
                  />
                  <p className="mt-1 text-xs text-textTertiary">{t('organizations.maxRecurringBookingsHelp')}</p>
                </div>

                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <label className="block text-sm font-medium text-textDefault mb-1">{t('organizations.noBookingsBefore')}</label>
                    <input
                      type="time"
                      value={formData.booking_no_before ? String(formData.booking_no_before).substring(0, 5) : ''}
                      onChange={(e) => setFormData((prev) => ({ ...prev, booking_no_before: e.target.value || null }))}
                      className="w-full px-3 py-2 border border-border rounded-lg bg-bgContainer text-textDefault"
                    />
                    <p className="mt-1 text-xs text-textTertiary">{t('organizations.noBookingsHelp')}</p>
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-textDefault mb-1">{t('organizations.noBookingsAfter')}</label>
                    <input
                      type="time"
                      value={formData.booking_no_after ? String(formData.booking_no_after).substring(0, 5) : ''}
                      onChange={(e) => setFormData((prev) => ({ ...prev, booking_no_after: e.target.value || null }))}
                      className="w-full px-3 py-2 border border-border rounded-lg bg-bgContainer text-textDefault"
                    />
                    <p className="mt-1 text-xs text-textTertiary">{t('organizations.noBookingsHelp')}</p>
                  </div>
                </div>

                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <label className="block text-sm font-medium text-textDefault mb-1">{t('organizations.minLengthHours')}</label>
                    <input
                      type="number"
                      min={0}
                      step={0.5}
                      value={formData.booking_min_length_hours ?? ''}
                      onChange={(e) => {
                        const v = e.target.value;
                        setFormData((prev) => ({
                          ...prev,
                          booking_min_length_hours: v === '' ? null : (parseFloat(v) || null),
                        }));
                      }}
                      placeholder="0"
                      className="w-full px-3 py-2 border border-border rounded-lg bg-bgContainer text-textDefault"
                    />
                    <p className="mt-1 text-xs text-textTertiary">{t('organizations.lengthHelp')}</p>
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-textDefault mb-1">{t('organizations.maxLengthHours')}</label>
                    <input
                      type="number"
                      min={0}
                      step={0.5}
                      value={formData.booking_max_length_hours ?? ''}
                      onChange={(e) => {
                        const v = e.target.value;
                        setFormData((prev) => ({
                          ...prev,
                          booking_max_length_hours: v === '' ? null : (parseFloat(v) || null),
                        }));
                      }}
                      placeholder="0"
                      className="w-full px-3 py-2 border border-border rounded-lg bg-bgContainer text-textDefault"
                    />
                    <p className="mt-1 text-xs text-textTertiary">{t('organizations.lengthHelp')}</p>
                  </div>
                </div>

                <div>
                  <label className="block text-sm font-medium text-textDefault mb-1">{t('organizations.bookingRestMinutes')}</label>
                  <input
                    type="number"
                    min={0}
                    value={formData.booking_rest_minutes ?? ''}
                    onChange={(e) => {
                      const v = e.target.value;
                      setFormData((prev) => ({
                        ...prev,
                        booking_rest_minutes: v === '' ? null : (parseInt(v, 10) ?? null),
                      }));
                    }}
                    placeholder="0"
                    className="w-full max-w-xs px-3 py-2 border border-border rounded-lg bg-bgContainer text-textDefault"
                  />
                  <p className="mt-1 text-xs text-textTertiary">{t('organizations.bookingRestMinutesHelp')}</p>
                </div>

                <div>
                  <label className="block text-sm font-medium text-textDefault mb-1">{t('organizations.bookingMinLeadTimeHours')}</label>
                  <input
                    type="number"
                    min={0}
                    step={1}
                    value={formData.booking_min_lead_time_hours ?? ''}
                    onChange={(e) => {
                      const v = e.target.value;
                      setFormData((prev) => ({
                        ...prev,
                        booking_min_lead_time_hours: v === '' ? null : (parseFloat(v) || null),
                      }));
                    }}
                    placeholder="0"
                    className="w-full max-w-xs px-3 py-2 border border-border rounded-lg bg-bgContainer text-textDefault"
                  />
                  <p className="mt-1 text-xs text-textTertiary">{t('organizations.bookingMinLeadTimeHoursHelp')}</p>
                </div>

                <div>
                  <label className="block text-sm font-medium text-textDefault mb-1">{t('organizations.bookableMonthsForward')}</label>
                  <input
                    type="number"
                    min={0}
                    max={24}
                    value={formData.booking_months_forward ?? ''}
                    onChange={(e) => {
                      const v = e.target.value;
                      setFormData((prev) => ({
                        ...prev,
                        booking_months_forward: v === '' ? null : (parseInt(v, 10) ?? null),
                      }));
                    }}
                    placeholder="0"
                    className="w-full max-w-xs px-3 py-2 border border-border rounded-lg bg-bgContainer text-textDefault"
                  />
                  <p className="mt-1 text-xs text-textTertiary">{t('organizations.bookableMonthsHelp')}</p>
                </div>

                <div>
                  <label className="block text-sm font-medium text-textDefault mb-1">{t('organizations.cancellationChargeHours')}</label>
                  <input
                    type="number"
                    min={0}
                    value={formData.cancellation_charge_hours ?? ''}
                    onChange={(e) => {
                      const v = e.target.value;
                      setFormData((prev) => ({
                        ...prev,
                        cancellation_charge_hours: v === '' ? null : (parseInt(v, 10) ?? null),
                      }));
                    }}
                    placeholder="0"
                    className="w-full max-w-xs px-3 py-2 border border-border rounded-lg bg-bgContainer text-textDefault"
                  />
                  <p className="mt-1 text-xs text-textTertiary">{t('organizations.cancellationChargeHelp')}</p>
                </div>

                <div>
                  <label className="block text-sm font-medium text-textDefault mb-1">{t('organizations.noShowReleaseMinutes')}</label>
                  <input
                    type="number"
                    min={0}
                    value={formData.no_show_release_minutes == null ? '' : String(formData.no_show_release_minutes)}
                    onChange={(e) => {
                      const v = e.target.value.trim();
                      setFormData((prev) => ({
                        ...prev,
                        no_show_release_minutes: v === '' ? 0 : (Number.isFinite(parseInt(v, 10)) ? parseInt(v, 10) : 0),
                      }));
                    }}
                    placeholder="0"
                    className="w-full max-w-xs px-3 py-2 border border-border rounded-lg bg-bgContainer text-textDefault"
                  />
                  <p className="mt-1 text-xs text-textTertiary">{t('organizations.noShowReleaseMinutesHelp')}</p>
                </div>

                <div>
                  <label className="block text-sm font-medium text-textDefault mb-1">{t('organizations.bookingPolicyHtml')}</label>
                  <p className="mt-0 mb-2 text-xs text-textTertiary">{t('organizations.bookingPolicyHtmlHelp')}</p>
                  <OrganizationBookingPolicyEditor
                    value={formData.booking_policy_html ?? ''}
                    onChange={(html) => setFormData((prev) => ({ ...prev, booking_policy_html: html }))}
                  />
                </div>
              </div>
            )}

            {editModalTab === 'authSettings' && editIsTopNode && (
              <div>
                <OrganizationFormFields
                  formData={formData}
                  setFormData={setFormData}
                  isTopNode={editIsTopNode}
                  onlySections={['authSettings']}
                />
              </div>
            )}

            {editModalTab === 'cabinetSettings' && editIsTopNode && (
              <OrganizationCabinetSettings organizationId={selectedOrganization.id} />
            )}

            {editModalTab === 'automaticService' && editIsTopNode && (
              <div className="p-4">
                <OrganizationAutoServiceSettings
                  data={{
                    auto_tire_service_enabled: (formData as any).auto_tire_service_enabled,
                    winter_tire_month: (formData as any).winter_tire_month,
                    winter_tire_day: (formData as any).winter_tire_day,
                    summer_tire_month: (formData as any).summer_tire_month,
                    summer_tire_day: (formData as any).summer_tire_day,
                    tire_service_advance_days: (formData as any).tire_service_advance_days,
                  }}
                  onChange={(updates) => setFormData((prev: any) => ({ ...prev, ...updates }))}
                />
              </div>
            )}

            {editModalTab === 'drivingLogSettings' && editIsTopNode && (
              <OrganizationDrivingLogSettings
                value={formData.driving_log_settings}
                onChange={(next) => setFormData((prev) => ({ ...prev, driving_log_settings: next }))}
              />
            )}

            {editModalTab === 'reminders' && editIsTopNode && (
              <OrganizationReminderSettings
                value={formData.reminder_settings}
                onChange={(next) => setFormData((prev) => ({ ...prev, reminder_settings: next }))}
              />
            )}
            {editModalTab === 'reportModels' && editIsTopNode && isGlobalAdmin && (
              <OrganizationReportModels organizationId={String(selectedOrganization.id)} />
            )}
            </div>
              </div>
            </div>

            <div className="flex justify-end gap-2 pt-4 mt-2 flex-shrink-0 border-t border-border">
              <button
                onClick={() => setShowEditModal(false)}
                className="px-4 py-2 bg-bgLight text-textDefault rounded-lg hover:bg-bgElevated transition-colors"
              >
                {t('common.cancel')}
              </button>
              <button
                onClick={handleUpdateOrganization}
                className="px-4 py-2 bg-bgPrimary text-textLight rounded-lg hover:opacity-90 transition-opacity"
              >
                {t('organizations.updateButton')}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Move Modal */}
      {showMoveModal && selectedOrganization && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
          <div className="bg-bgElevated rounded-lg p-6 max-w-md w-full">
            <h2 className="text-2xl font-bold text-textDefault mb-4">
              {t('organizations.moveOrganization') || 'Flytta organisation'}
            </h2>
            <p className="text-textSecondary mb-4">
              {t('organizations.moveOrganizationDescription') || `Flytta "${selectedOrganization.name}" och alla dess underorganisationer till en ny föräldraorganisation. Alla inställningar behålls.`}
            </p>

            <div className="space-y-4">
              <div>
                <label className="block text-sm font-medium text-textDefault mb-1">
                  {t('organizations.newParent') || 'Ny föräldraorganisation'} *
                </label>
                
                <HierarchicalOrgSelect
                  value={String(moveTargetParentId || '')}
                  onChange={(value) => setMoveTargetParentId(value)}
                  placeholder={t('organizations.selectParent') || 'Välj föräldraorganisation'}
                  emptyLabel={t('organizations.topNode') || 'Toppnod (ingen förälder)'}
                  excludeIds={moveExcludeIds.map((id) => String(id))}
                />
              </div>
            </div>

            <div className="flex justify-end gap-2 mt-6">
              <button
                onClick={() => {
                  setShowMoveModal(false);
                  setMoveTargetParentId('');
                }}
                className="px-4 py-2 bg-bgLight text-textDefault rounded-lg hover:bg-bgElevated transition-colors"
              >
                {t('common.cancel')}
              </button>
              <button
                onClick={handleMoveOrganization}
                disabled={!moveTargetParentId}
                className="px-4 py-2 bg-bgPrimary text-textLight rounded-lg hover:opacity-90 transition-opacity disabled:opacity-50 disabled:cursor-not-allowed"
              >
                {t('organizations.moveButton') || 'Flytta'}
              </button>
            </div>
          </div>
        </div>
      )}

      <ConfirmDialog />

      {importTargetOrg && (
        <OrganizationImportModal
          parentOrg={importTargetOrg}
          onClose={() => setImportTargetOrg(null)}
          onSuccess={() => {
            setImportTargetOrg(null);
            // Ladda om trädet
            window.location.reload();
          }}
        />
      )}

      {showLogoCropper && selectedOrganization && (
        <LogoCropperModal
          organizationName={selectedOrganization.name}
          onSave={handleSaveCroppedLogo}
          onClose={() => {
            if (!logoUploading) setShowLogoCropper(false);
          }}
        />
      )}
    </div>
  );
};

export default OrganizationsManagement;
